import { type MouseEvent } from 'react'
import { Box, Body1, Badge, BadgeInline, StackRow, Ellipsis } from '@dhl-ui/components'
import { type PageMenuItem } from './types'
import { Facet } from './Facet'
import { MainMenuItemTooltip } from './MainMenuItemTooltip'
import { SideMenuItem } from './SideMenuItem'

type Props = Pick<PageMenuItem, 'label' | 'icon' | 'badge' | 'link' | 'isDisabled' | 'isActive'> & {
  /**
   * Unique identifier
   */
  id: string

  /**
   * Is our expandable menu open or not
   */
  isOpen?: boolean

  /**
   * When our item has sub items
   */
  isExpandable?: boolean

  /**
   * is our main menu expanded or not
   */
  isExpanded: boolean

  /**
   * When our user selects a menu item
   */
  onClick: (event: MouseEvent<HTMLElement>) => void

  /**
   * flag when
   * used for aria control
   */
  isCollapsedMenuOpenForItem?: boolean
}

export const MainMenuItem = ({
  id,
  icon,
  isActive,
  isDisabled,
  link,
  isExpandable,
  isOpen,
  isExpanded,
  label,
  badge,
  onClick,
  isCollapsedMenuOpenForItem,
}: Props) => (
  <MainMenuItemTooltip label={label} isExpanded={isExpanded}>
    <SideMenuItem
      id={id}
      link={link}
      onClick={onClick}
      isDisabled={isDisabled}
      isActive={isActive}
      isExpanded={isExpanded}
      isCollapsedMenuOpenForItem={isCollapsedMenuOpenForItem && isOpen}
      aria-label={isExpanded ? undefined : label}
    >
      {isExpanded ? (
        <StackRow gap={1} sx={{ width: '100%' }}>
          <StackRow gap={1.5} sx={{ width: '100%', minWidth: 0 }}>
            <Box
              sx={({ spacing }) => ({
                color: isActive ? 'primary.main' : 'grey.700',
                width: spacing(3),
                display: 'inline-flex',
                svg: { fontSize: spacing(3) },
              })}
            >
              {icon}
            </Box>

            <Ellipsis sx={{ flex: '1' }}>
              <Body1 component="span" variant="inherit" weight="inherit">
                {isActive ? <strong>{label}</strong> : label}
              </Body1>
            </Ellipsis>
          </StackRow>

          {badge && <BadgeInline variant="standard" badgeContent={badge.count} color={badge.color} />}

          {isExpandable ? <Facet isOpen={isOpen} /> : null}
        </StackRow>
      ) : (
        <Box
          sx={({ spacing }) => ({
            color: isActive ? 'primary.main' : 'grey.700',
            minWidth: 'auto',
            display: 'inline-flex',
            svg: { fontSize: spacing(3) },
            p: 0,
          })}
        >
          {badge ? (
            <Badge badgeContent={badge.count} color={badge.color}>
              {icon}
            </Badge>
          ) : (
            icon
          )}

          {isExpandable ? <Facet inverse /> : null}
        </Box>
      )}
    </SideMenuItem>
  </MainMenuItemTooltip>
)
